import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";

import { AccordionButton } from "@/components/Button/AccordionButton";
import ActionButton from "@/components/Button/ActionButton";
import { AttributeCard } from "@/components/Card/AttributeCard";
import Icon from "@/components/Icon/Icon";
import type { IconName } from "@/components/Icon/iconMap";
import { Grid, Spacer, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { defaultNS, useTranslation } from "@/lib/i18n";

export const Route = createFileRoute("/_staff/fleets/add-select/")({
  component: FleetTypeSelection,
});

type CommonProps = {
  selectedFleetType: string | null;
  setSelectedFleetType: Dispatch<SetStateAction<string | null>>;
};

function FleetTypeSelection() {
  const [selectedFleetType, setSelectedFleetType] = useState<string | null>(
    "automobile",
  );

  return (
    <VStack className="mx-auto w-[1000px]">
      <Header />
      <Spacer variant="xs" />
      <FleetTypeRadioGroup
        selectedFleetType={selectedFleetType}
        setSelectedFleetType={setSelectedFleetType}
      />
      <Spacer variant="xxl" />
      <AdditionalOptions />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.fleets.addSelect.header",
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <Text variant="h2">{t("headerTitle")}</Text>
    </VStack>
  );
}

function FleetTypeRadioGroup({
  selectedFleetType,
  setSelectedFleetType,
}: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.fleets.addSelect.body",
  });

  const FLEET_TYPES: {
    id: string;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
  }[] = [
    {
      id: "automobile",
      title: t("automobile"),
      requiredDetails: [
        { iconName: "SquareDot", text: t("itemCard.fleetName") },
      ],
    },
    {
      id: "ship",
      title: t("ship"),
      requiredDetails: [
        { iconName: "SquareDot", text: t("itemCard.fleetName") },
      ],
    },
    // 航空機は一時的にスコープアウト
    // {
    //   id: "aircraft",
    //   title: t("aircraft"),
    //   requiredDetails: [
    //     { iconName: "SquareDot", text: t("itemCard.fleetName") },
    //   ],
    // },
  ];

  const navigate = useNavigate();

  const handleRadioChange = useCallback(
    (value: string) => {
      setSelectedFleetType(value);
    },
    [setSelectedFleetType],
  );

  const handleButtonClick = useCallback(() => {
    if (selectedFleetType === "automobile") {
      // 自動車の場合は /fleets/add へ遷移（mainType=VEHICLEを渡す）
      navigate({
        to: "/fleets/add",
        search: {
          mainType: "VEHICLE",
        },
        hash: "basic",
      });
    } else if (selectedFleetType === "ship") {
      // 船舶の場合
      navigate({
        to: "/fleets/add",
        search: {
          mainType: "SHIP",
        },
        hash: "ship-basic",
      });
    } else if (selectedFleetType === "aircraft") {
      // 航空機の場合（将来実装予定）
      navigate({
        to: "/fleets/add",
        search: {
          mainType: "AIRCRAFT",
        },
        hash: "basic",
      });
    }
  }, [selectedFleetType, navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4">{t("selectFleetType")}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange} defaultValue="automobile">
        <Grid columns={2} rowGap={28} columnGap={24}>
          {FLEET_TYPES.filter((type) => type.id !== "aircraft").map((type) => (
            <AttributeCard
              key={type.id}
              title={type.title}
              titleStyle="CardHeader"
              onClick={() => handleRadioChange(type.id)}
              actionButton={
                <RadioGroupItem
                  id={type.id}
                  value={type.id}
                  className="h-4 w-4"
                  checked={selectedFleetType === type.id}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className="cursor-pointer rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md"
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t("itemCard.requiredDetails")}
                </Text>
                {type.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t("fillOutDetails")}
      </ActionButton>
    </>
  );
}

function AdditionalOptions() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.fleets.addSelect.options",
  });

  const additionalOptions = [
    { textKey: "syncFleetManagement" },
    { textKey: "bulkCsvUpload.title" },
  ] as const;

  return (
    <VStack className="w-full">
      <Text variant="labelSemiLarge">{t("description")}</Text>
      <Spacer size={24} />
      <VStack className="w-full">
        {additionalOptions.map(({ textKey }, index) => (
          <div key={textKey}>
            {index > 0 && <Spacer size={24} />}
            <AccordionButton
              icon={
                <Icon
                  iconName="DatabaseBackup"
                  text={t(textKey)}
                  textColor="text-primary"
                  colorVariant="primary"
                />
              }
              inDevelopment
              className="w-full"
            />
          </div>
        ))}
      </VStack>
    </VStack>
  );
}
